/**
 *  Cart
 *    Functions to handle all cart events.
 *
 *  Version
 *    1.0.1  - 2021/12/16
 */

import $ from 'jquery';
import {
  getCurrentCart,
  printMoney,
  changeCart,
  cartAdd,
  ON_CART_FINISHED,
  removeFromCart,
  pictureGenerate,
} from '@process-creative/slate-theme-tools';

import { t } from '../../tools/utilities/string-tools';
import { shopifyAjaxErrorGet } from '../../tools/utilities/error';
import {
  CLASS_SCROLL_DISABLED,
  CLASS_DRAWER_OPEN,
  CLASS_CART_PENDING,
  CLASS_INITIALISED,
  CLASS_CART_PAGE,
  CLASS_CART_EMPTY,
} from '../../tools/constants/classes';
import { findTarget } from '../../tools/utilities/event-tools';
import { Cart, LineItem, Product } from '../../types';
import { RecommendedProduct } from '../../types/recommended-product';
import {
  initCarousel,
  CarouselOptions,
  InitCarouselParams,
} from '../../objects/carousel';
import {
  responsiveMediaAbove,
  responsiveMediaBelow,
} from '../../settings/responsive';

import { checkFreeGifts } from './cart-free-gifts';
import '../subscription-selector/subscription-selector';
import { trapFocus, trapFocusDelay, removeTrapFocus } from '../../tools/utilities/trap-focus';

// Selectors
const SELECTOR_CONTAINER = '[data-cart-container]';
const SELECTOR_PRODUCT = '[data-cart-product]';
const SELECTOR_OPEN = '[data-cart-drawer-open]';
const SELECTOR_CLOSE = '[data-cart-drawer-close]';
const SELECTOR_OVERLAY = '[data-cart-drawer-overlay]';
const SELECTOR_SHOPIFY_SECTION = '#shopify-section-cart-contents';
const SELECTOR_UPSELLS = '[data-cart-upsells]';
const SELECTOR_UPSELL_ADD = '[data-cart-upsell-add-btn]';
const SELECTOR_UPSELL_PRODUCT = '[data-cart-upsell-product]';
const SELECTOR_REMOVE_BTN = '[data-cart-remove-btn]';
const SELECTOR_QTY_INPUT = '[data-quantity-selector-field]';
const SELECTOR_QTY_PLUS = '[data-quantity-selector-plus]';
const SELECTOR_QTY_MINUS = '[data-quantity-selector-minus]';
const SELECTOR_UPSELLS_CONTAINER = '[data-cart-upsells-container]';
const SELECTOR_DRAWER_DATA = '[data-cart-drawer-data]';
const SELECTOR_LINE_ITEM_JSON = '[data-cart-item-json]';

// Selectors for Cart Page Carousel on Mobile
const SELECTOR_CAROUSEL = '[data-cart-upsells]';
const SELECTOR_NAVIGATION = '[data-carousel-navigation]';
const SELECTOR_SLIDE = '[data-cart-upsell-product]';

// Attributes
const ATTR_VARIANT_ID = 'data-variant-id';
const ATTR_LINE_ITEM_INDEX = 'data-cart-remove';
const ATTR_PRODUCT_ID = 'data-cart-product';
const ATTR_CART_TEMPLATE = 'data-cart-template';

// products to fetch
const UPSELL_PRODUCTS_FETCH_LIMIT = 10;
// products to display
let upsellProductsLimit = 3;
const upsellProductRecommendations = false;

const body = document.body!;

document
  .querySelectorAll<HTMLDivElement>(SELECTOR_CONTAINER)
  .forEach(async (container) => {
    let contentsRendered = false;
    let cartOpen = false;
    let cartPage = false;

    /**
     *  Elements
     */
    let openBtn: HTMLButtonElement;
    let closeBtn: HTMLButtonElement;
    let overlay: HTMLElement;
    let upsellAddBtns: NodeListOf<HTMLButtonElement>;
    let removeButtons: NodeListOf<HTMLButtonElement>;
    let qtyPlusBtns: NodeListOf<HTMLElement>;
    let qtyMinusBtns: NodeListOf<HTMLElement>;

    // Render upsells on cart page
    if (container.hasAttribute(ATTR_CART_TEMPLATE)) cartPage = true;

    if (upsellProductRecommendations && cartPage) {
      await addUpsells();
    } else {
      await renderCartContents();
      removeFreeGiftIfUnavailableGWP();
    }

    addEventListeners();

    $(document).on(ON_CART_FINISHED, onCartFinished);

    document.addEventListener('refreshCart', () => renderCartContents());

    function addEventListeners() {
      refreshElements();

      openBtn.addEventListener('click', onCartToggle);
      closeBtn.addEventListener('click', onCartToggle);
      overlay.addEventListener('click', onCartToggle);

      container.addEventListener('keydown', closeCartOnEsc);

      upsellAddBtns.forEach((btn) =>
        btn.addEventListener('click', onUpsellAdd)
      );
      removeButtons.forEach((btn) =>
        btn.addEventListener('click', onItemRemove)
      );

      qtyPlusBtns.forEach((btn) => btn.addEventListener('click', onQtyPlus));
      qtyMinusBtns.forEach((btn) => btn.addEventListener('click', onQtyMinus));
    }

    function removeEventListeners() {
      if (openBtn)
        openBtn.removeEventListener('click', onCartToggle);
      if (closeBtn)
        closeBtn.removeEventListener('click', onCartToggle);
      if (overlay)
        overlay.removeEventListener('click', onCartToggle);
      if (container)
        container.removeEventListener('keydown', closeCartOnEsc);

      if (upsellAddBtns)
        upsellAddBtns.forEach((btn) =>
          btn.removeEventListener('click', onUpsellAdd)
        );
      if (removeButtons)
        removeButtons.forEach((btn) =>
          btn.removeEventListener('click', onItemRemove)
        );
      if (qtyPlusBtns)
        qtyPlusBtns.forEach((btn) =>
          btn.removeEventListener('click', onQtyPlus)
        );
      if (qtyMinusBtns)
        qtyMinusBtns.forEach((btn) =>
          btn.removeEventListener('click', onQtyMinus)
        );
    }

    /**
     * Assigns variables
     */
    function refreshElements() {
      openBtn = document.querySelector(SELECTOR_OPEN);
      closeBtn = document.querySelector(SELECTOR_CLOSE);
      overlay = document.querySelector(SELECTOR_OVERLAY);
      upsellAddBtns = container.querySelectorAll(SELECTOR_UPSELL_ADD);
      removeButtons = container.querySelectorAll(SELECTOR_REMOVE_BTN);
      qtyPlusBtns = container.querySelectorAll(SELECTOR_QTY_PLUS);
      qtyMinusBtns = container.querySelectorAll(SELECTOR_QTY_MINUS);
    }

    /**
     * Calls function to render cart
     * Opens the cart if it is not open and not on the cart page
     */
    function onCartFinished() {
      renderCartContents();

      // open cart if it's not open and we're not on the cart page
      if (!cartOpen && !isCartPage()) setTimeout(() => openCart(), 500);
      
      // if cart is open, trap focus on the close button after cart finishes updating
      setTimeout(() => {
        if (cartOpen && !isCartPage()) trapFocus(container, closeBtn, true);
      }, 800);
    }

    /**
     * Fetches first item in the cart
     * Fetches recommended products based on that product's ID
     * Creates HTML string of upsell product thumbnails
     * Returns HTML string
     */
    async function renderUpsellProducts() {
      let html = '';
      const cartFetch = getCurrentCart() as Cart;
      if (!cartFetch.items.length) return undefined;
      const productId = cartFetch.items[0].product_id;
      const productIds = cartFetch.items.map(
        (item: LineItem) => item.product_id
      );

      const upsellProducts: Product[] = [];

      if (upsellProducts.length < UPSELL_PRODUCTS_FETCH_LIMIT) {
        const upsellProductsFetch: RecommendedProduct = await fetch(
          `/recommendations/products.json?product_id=${productId}&limit=${UPSELL_PRODUCTS_FETCH_LIMIT}`
        ).then((res) => res.json());
        upsellProductsFetch.products.forEach((prod) => {
          upsellProducts.push(prod);
        });
      }
      const upsellProductIds = upsellProducts.map((item) => item.id);

      // Loop through upsell products to remove
      // any of the products that are already in the cart
      upsellProductIds.forEach((id, i) => {
        if (productIds.includes(id)) {
          upsellProducts.splice(i, 1);
        }
      });

      if (upsellProducts.length === 0) {
        html = '';
      } else {
        // Cut down to the number of products to display
        upsellProducts.length = upsellProductsLimit;

        upsellProducts.forEach((product) => {
          const { featured_image, image, price, url, title, variants } =
            product;
          const variantId = variants[0].id;
          const formattedPrice = price
            ? printMoney(price)
            : printMoney(variants[0].price);
          const add = t('general.cart.add');
          const picture = pictureGenerate({
            src: image || featured_image || '',
            srcSize: 60,
            alt: product.title,
            class: 'c-cart-upsells__image o-responsive-image',
          });

          html += `
          <div class="c-cart-upsells__product test-ts" data-cart-upsell-product data-variant-id="${variantId}">
            <div class="c-cart-upsells__image-container o-responsive-image-container">
              <a href="${url}">${picture}</a>
            </div>
            <div class="c-cart-upsells__details-wrapper">
              <div class="c-cart-upsells__details">
                <p class="c-cart-upsells__product-title">${title}</p>
                <p class="c-cart-upsells__price">
                  <span class="c-cart-upsells__price-wrapper"><span class="o-subheading">${formattedPrice}</span>
                </p>
              </div>
              <div class="c-cart-upsells__add-button o-paragraph--2">
                <button class="o-btn is-upsell" data-cart-upsell-add-btn>
                  ${add}
                </button>
              </div>
            </div>
          </div>
        `;
        });
      }
      return html;
    }

    /**
     * Toggles open/close state of cart drawer
     * @param evt click event
     */
    function onCartToggle(evt: Event) {
      evt.preventDefault();

      if (cartOpen) {
        closeCart();
      } else {
        if (!contentsRendered) renderCartContents();
        contentsRendered = true;
        openCart();
      }
    }

    /**
     * Adds upsell product to cart
     * Updates button text
     *
     * @param evt Click event
     */
    async function onUpsellAdd(evt: Event) {
      const button = findTarget(evt, SELECTOR_UPSELL_ADD);
      const upsellProduct = button?.closest<HTMLElement>(
        SELECTOR_UPSELL_PRODUCT
      );
      if (!upsellProduct) return;
      const sellingPlanInput = upsellProduct.querySelector<HTMLInputElement>('[data-subscription-selector-selling-plan-id]');
      const variantID = upsellProduct.getAttribute(ATTR_VARIANT_ID);
      if (!variantID)
        throw new Error(
          'Invalid markup detected in the product upsell snippet.'
        );

      try {
        if (button) button.innerText = t('general.cart.adding');
        await cartAdd({
          items: [
            {
              id: parseInt(variantID, 10),
              quantity: 1,
              selling_plan: sellingPlanInput ? sellingPlanInput.value : null,
            },
          ],
        });
        await checkFreeGifts(parseInt(variantID, 10));
        onCartFinished();
      } catch (err) {
        // Error, show error, enable buttons hide loader
        console.error('Failed to add upsell to cart.');
      }
    }

    /**
     * Removes item from cart based on the item's line index
     * @param evt Click event
     */
    async function onItemRemove(evt: Event) {
      evt.preventDefault();
      body.classList.add(CLASS_CART_PENDING);
      const button = evt.currentTarget as HTMLElement;
      const lineindex = button.getAttribute(ATTR_LINE_ITEM_INDEX);
      if (!lineindex) return;
      const li = parseInt(lineindex, 10);

      const parent = findTarget(evt, SELECTOR_PRODUCT);
      if (!parent) return;

      const jsonElem = parent.querySelector<HTMLScriptElement>(
        SELECTOR_LINE_ITEM_JSON
      );
      if (!jsonElem) return;

      const variantId = parent.getAttribute(ATTR_PRODUCT_ID);
      if (!variantId) return;

      try {
        await removeFromCart(li);
        onCartFinished();
      } catch (err) {
        console.error(shopifyAjaxErrorGet(err));
      }
      setTimeout(() => body.classList.remove(CLASS_CART_PENDING), 300);
    }

    /**
     *
     * @param evt click event
     * @param change string that determines if you're adding or subtracting. Must be add or subtract
     * Get the index of the cart item clicked on, then adds or removes 1 from the quantity depending on argument passed in
     */
    async function onQtyChange(evt: Event, change: 'add' | 'subtract') {
      const target = evt.currentTarget as HTMLElement;
      const parent = target.closest(SELECTOR_PRODUCT);
      let quantity;
      if (!parent) return;

      const variant = parent.getAttribute('data-cart-product');
      const key = parent.getAttribute('data-cart-product-key');
      const inputQuantity =
        parent.querySelector<HTMLInputElement>(SELECTOR_QTY_INPUT);
      if (!variant || !inputQuantity) return;

      if (change === 'add') {
        quantity = parseInt(inputQuantity.value, 10) + 1;
      } else if (change === 'subtract') {
        quantity = parseInt(inputQuantity.value, 10) - 1;
      }
      if (quantity) {
        await changeCart({ key, quantity });
        onCartFinished();
      }
    }

    function onQtyPlus(evt: Event) {
      onQtyChange(evt, 'add');
    }

    function onQtyMinus(evt: Event) {
      onQtyChange(evt, 'subtract');
    }

    /**
     * Adds a class to open cart drawer
     * Adds class to body to disable scrolling
     */
    function openCart() {
      container.classList.add(CLASS_DRAWER_OPEN);
      body.classList.add(CLASS_SCROLL_DISABLED);
      cartOpen = true;

      container.addEventListener('transitionend', (e) => {
        trapFocus(container, closeBtn, true);
      }, { once: true });
    }

    /**
     * Removes a class to close cart drawer
     * Removes class to body to enable scrolling
     */
    function closeCart() {
      container.classList.remove(CLASS_DRAWER_OPEN);
      body.classList.remove(CLASS_SCROLL_DISABLED);
      cartOpen = false;

      container.addEventListener('transitionend', (e) => {
        removeTrapFocus(openBtn, true);
      }, { once: true });
    }

    /**
     * Checks if you're currently on the cart page
     */
    function isCartPage() {
      return window.location.href.includes('/cart');
    }

    /**
     * Closes cart when escape key is pressed
     * @param evt: KeyboardEvent
     */
    function closeCartOnEsc(evt: KeyboardEvent) {
      if (evt.key === 'Escape' || evt.key === 'Esc') {
        if (cartOpen) {
          closeCart();
        }
      }
    }

    /**
     * Grabs the text entered into textareas
     * Gets the checked state of the checkbox
     * Updates cart attributes
     */

    async function addUpsells() {
      const upsellContainer = container.querySelector<HTMLDivElement>(
        SELECTOR_UPSELLS_CONTAINER
      );
      const upsellElement =
        container.querySelector<HTMLDivElement>(SELECTOR_UPSELLS);

      // If there's nothing in the upsell container, update the number
      if (upsellElement) {
        const upsellProducts = upsellElement.querySelectorAll(
          SELECTOR_UPSELL_PRODUCT
        );
        if (isCartPage()) {
          upsellProductsLimit = 2;
        } else if (!upsellProducts.length && !isCartPage()) {
          upsellProductsLimit = 1;
        } else {
          upsellProductsLimit = 0;
        }
      }

      const upsellHtml = await renderUpsellProducts();

      // Inject the fetched product(s).
      // If upsellHtml doesn't return anything because all
      // fetched products are already in the cart, then hide the upsells
      if (upsellElement && upsellHtml) {
        upsellElement.innerHTML += upsellHtml;
      } else if (upsellContainer && !upsellElement) {
        upsellContainer.style.display = 'none';
      }

      const count = getCurrentCart().item_count;
      const cartEmptyState = count === 0;

      // carousel functions

      const carouselElement =
        container.querySelector<HTMLElement>(SELECTOR_CAROUSEL);
      const navigation =
        container.querySelector<HTMLElement>(SELECTOR_NAVIGATION);

      if (!carouselElement || (cartEmptyState && !isCartPage()) || !navigation)
        return;

      // Set carousel options
      const options: CarouselOptions = {
        slidesPerView: 1,
        loop: true,
        centered: true,
        spacing: 16,
      };

      // Set carousel parameters
      const params: InitCarouselParams = {
        container: carouselElement,
        navigationContainer: navigation,
        options,
      };

      // Initialize carousel only on mobile
      const carousel = initCarousel(params);

      if (!carousel) return;
      const slider = carousel.slider;

      // Remove carousel functionality on medium breakpoint and above on resize and load
      const onResize = () => {
        if (
          responsiveMediaAbove('LARGE') &&
          carouselElement.classList.contains(CLASS_INITIALISED)
        ) {
          carouselElement.classList.remove('keen-slider');
          carouselElement
            .querySelectorAll<HTMLElement>(SELECTOR_SLIDE)
            .forEach((slide) => slide.classList.remove('keen-slider__slide'));
          slider.destroy();
        } else if (
          responsiveMediaBelow('LARGE') &&
          !carouselElement.classList.contains(CLASS_INITIALISED)
        ) {
          carouselElement.classList.add('keen-slider');
          carouselElement
            .querySelectorAll<HTMLElement>(SELECTOR_SLIDE)
            .forEach((slide) => slide.classList.add('keen-slider__slide'));
          slider.refresh();
        }
      };
      onResize();

      window.addEventListener('load', onResize);
      window.addEventListener('resize', onResize);

      // add class on carousel nav to hide on cart drawer
      if (isCartPage()) {
        navigation.classList.add(CLASS_CART_PAGE);
      } else {
        navigation.classList.remove(CLASS_CART_PAGE);
      }

      // add class on carousel nav to identify is the cart is empty or not
      if (cartEmptyState) {
        navigation.classList.add(CLASS_CART_EMPTY);
      } else {
        navigation.classList.remove(CLASS_CART_EMPTY);
      }
    }
    /**
     *  renderCartContents
     *    Uses the Section Rendering API to render the contents of the cart
     *    drawer and print them in the container for the cart drawer
     */
    async function renderCartContents() {
      // Set global pending state
      body.classList.add(CLASS_CART_PENDING);

      // Remove event listeners and destroy existing upsell sliders
      removeEventListeners();

      const sectionHtml: string = await fetch(`/?sections=cart-contents`)
        .then((res) => {
          return res.json();
        })
        .then((res) => res['cart-contents']);
      const html = document.createElement('div');
      html.innerHTML = sectionHtml;
      const sectionWrapper = html.querySelector<HTMLDivElement>(
        SELECTOR_SHOPIFY_SECTION
      );
      if (!sectionWrapper)
        throw new Error('Invalid markup detected in the Cart Drawer section.');
      if (cartPage) {
        container.innerHTML = sectionHtml;
      } else {
        container.innerHTML = String(sectionWrapper.innerHTML);
      }

      if (upsellProductRecommendations) {
        await addUpsells();
      }

      // DOM changed, refresh listeners
      addEventListeners();

      // Remove global pending state
      body.classList.remove(CLASS_CART_PENDING);

      // Validate free gifts
      validateFreeGifts();
    }
    /**
     *  validateFreeGifts
     *    Validate that our free gift items are discounted to 0, if not remove them from the cart.
     */
    function validateFreeGifts() {
      const freeGifts = document.querySelectorAll(
        '[data-cart-product-free-gift]'
      );

      freeGifts.forEach((gift) => {
        const giftPrice = gift.getAttribute(
          'data-cart-product-free-gift-price'
        );
        const giftPriceInt = parseInt(giftPrice, 10);
        const removeButton = gift.querySelector<HTMLButtonElement>('[data-cart-remove]');

        if (giftPriceInt === 0 || !removeButton) {
          return;
        }

        removeButton.click();
      });
    }

    function removeFreeGiftIfUnavailableGWP() {
      const freeGiftsData = document.querySelectorAll(
        '[data-cart-product-free-gift]'
      );
      const freeGiftChecker = document.querySelector('[data-available-gwp]');
      const value = freeGiftChecker?.getAttribute('data-available-gwp');

      if (freeGiftsData && value === 'false') {
        freeGiftsData.forEach((gift) => {
          const removeButton = gift.querySelector<HTMLButtonElement>('[data-cart-remove]');

          if (!removeButton) {
            return;
          }

          removeButton.click();
        });
      }
    }
  });
